import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../utils/axiosConfig';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Proje {
  projeID: number;
  projeAdi: string;
}

interface Cari {
  cariID: number;
  adiSoyadi: string;
  telefon1?: string;
}

interface KPIs {
  gecikmisToplam: number;
  gecikmisSayi: number;
  bugunToplam: number;
  bugunSayi: number;
  toplamBeklenen: number;
  toplamBeklenenSayi: number;
}

interface TaksitItem {
  taksitID: number;
  sozlesmeID: number;
  sozlesmeTakipNo: string | null;
  cariID: number;
  musteriAdi: string;
  projeAdi: string;
  projeID: number | null;
  vadeTarihi: string;
  tahsilatTarihi: string | null;
  taksitTutari: number;
  kalanTutar: number;
  odemeTuruDetay: string;
  siraNo: number;
  durum: 'Gecikmiş' | 'Bugün' | 'Bekleniyor' | 'Ödendi' | 'İptal';
  odemeDurumu: string;
}

interface PanelData {
  kpis: KPIs;
  taksitListesi: TaksitItem[];
}

// ─── Sabitler ───────────────────────────────────────────────────────────────
const DURUM_OPTIONS = ['Gecikmiş', 'Bugün', 'Bekleniyor', 'Ödendi', 'İptal'] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatCurrency = (val: number) =>
  new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
  }).format(val);

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
const statusConfig: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  'Gecikmiş': {
    label: 'Gecikmiş',
    bg: 'bg-red-50',
    text: 'text-red-700',
    dot: 'bg-red-500',
  },
  'Bugün': {
    label: 'Bugün',
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    dot: 'bg-orange-500',
  },
  'Bekleniyor': {
    label: 'Bekleniyor',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-400',
  },
  'Ödendi': {
    label: 'Ödendi',
    bg: 'bg-green-50',
    text: 'text-green-700',
    dot: 'bg-green-500',
  },
  'İptal': {
    label: 'İptal',
    bg: 'bg-gray-100',
    text: 'text-gray-500',
    dot: 'bg-gray-400',
  },
};

const StatusBadge: React.FC<{ durum: string }> = ({ durum }) => {
  const cfg = statusConfig[durum] ?? {
    label: durum,
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    dot: 'bg-gray-400',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const TaksitTakipPaneli: React.FC = () => {
  const navigate = useNavigate();

  // Filter state
  const [projeler, setProjeler] = useState<Proje[]>([]);
  const [cariler, setCariler] = useState<Cari[]>([]);
  const [musteriArama, setMusteriArama] = useState('');
  const [filteredCariler, setFilteredCariler] = useState<Cari[]>([]);
  const [showCariDropdown, setShowCariDropdown] = useState(false);

  const todayStr = new Date().toISOString().split('T')[0];

  const [filters, setFilters] = useState<{
    projeID: string;
    cariID: string;
    cariAdi: string;
    baslangicTarihi: string;
    bitisTarihi: string;
    odemeDurumu: string[];
  }>({
    projeID: '',
    cariID: '',
    cariAdi: '',
    baslangicTarihi: '',
    bitisTarihi: todayStr,
    odemeDurumu: ['Gecikmiş', 'Bugün', 'Bekleniyor'],
  });
  const [showDurumDropdown, setShowDurumDropdown] = useState(false);

  // Data state
  const [panelData, setPanelData] = useState<PanelData | null>(null);
  const [loading, setLoading] = useState(false);
  const [tableSearch, setTableSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 15;

  // ── Initial data load ────────────────────────────────────────────────────
  useEffect(() => {
    fetchProjeler();
    fetchCariler();
    fetchPanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProjeler = async () => {
    try {
      const res = await axiosInstance.get('/api/TaksitTakip/projeler');
      setProjeler(res.data);
    } catch {
      // sessiz hata
    }
  };

  const fetchCariler = async () => {
    try {
      const res = await axiosInstance.get('/api/TaksitTakip/cariler');
      setCariler(res.data);
      setFilteredCariler(res.data);
    } catch {
      // sessiz hata
    }
  };

  const fetchPanel = useCallback(async (overrideFilters?: typeof filters) => {
    setLoading(true);
    const f = overrideFilters ?? filters;
    try {
      const params: Record<string, string> = {};
      if (f.projeID) params.projeID = f.projeID;
      if (f.cariID) params.cariID = f.cariID;
      if (f.baslangicTarihi) params.baslangicTarihi = f.baslangicTarihi;
      if (f.bitisTarihi) params.bitisTarihi = f.bitisTarihi;
      if (f.odemeDurumu.length) params.odemeDurumu = f.odemeDurumu.join(',');

      const res = await axiosInstance.get('/api/TaksitTakip/panel', { params });
      setPanelData(res.data);
      setCurrentPage(1);
    } catch (err: any) {
      toast.error('Veriler yüklenemedi: ' + (err?.response?.data?.message ?? err?.message ?? ''));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // ── Müşteri arama filtresi ────────────────────────────────────────────────
  const handleMusteriArama = (val: string) => {
    setMusteriArama(val);
    if (!val) {
      setFilteredCariler(cariler);
      setFilters(prev => ({ ...prev, cariID: '', cariAdi: '' }));
      return;
    }
    const lower = val.toLowerCase();
    setFilteredCariler(cariler.filter(c => c.adiSoyadi.toLowerCase().includes(lower)));
    setShowCariDropdown(true);
  };

  const selectCari = (c: Cari) => {
    setMusteriArama(c.adiSoyadi);
    setFilters(prev => ({ ...prev, cariID: String(c.cariID), cariAdi: c.adiSoyadi }));
    setShowCariDropdown(false);
  };

  const clearCariFilter = () => {
    setMusteriArama('');
    setFilters(prev => ({ ...prev, cariID: '', cariAdi: '' }));
    setFilteredCariler(cariler);
  };

  // ── Filtre uygula / temizle ───────────────────────────────────────────────
  const handleFiltrele = () => {
    fetchPanel(filters);
  };

  const handleTemizle = () => {
    const cleared = {
      projeID: '',
      cariID: '',
      cariAdi: '',
      baslangicTarihi: '',
      bitisTarihi: todayStr,
      odemeDurumu: ['Gecikmiş', 'Bugün', 'Bekleniyor'],
    };
    setFilters(cleared);
    setMusteriArama('');
    setTableSearch('');
    fetchPanel(cleared);
  };

  // ── KPI kart tıklama (durum filtresi) ────────────────────────────────────
  // Karta tıklayınca yalnızca o durum seçilir; zaten tek başına seçiliyse
  // varsayılan üçlü seçime geri dönülür.
  const handleKpiClick = (durum: string) => {
    const isIsolated = filters.odemeDurumu.length === 1 && filters.odemeDurumu[0] === durum;
    const newFilters = {
      ...filters,
      odemeDurumu: isIsolated ? ['Gecikmiş', 'Bugün', 'Bekleniyor'] : [durum],
    };
    setFilters(newFilters);
    fetchPanel(newFilters);
  };

  // ── Durum çoklu seçim toggle ──────────────────────────────────────────────
  const toggleDurum = (durum: string) => {
    setFilters(prev => ({
      ...prev,
      odemeDurumu: prev.odemeDurumu.includes(durum)
        ? prev.odemeDurumu.filter(d => d !== durum)
        : [...prev.odemeDurumu, durum],
    }));
  };

  // ── Tablo filtrelemesi + sayfalama ────────────────────────────────────────
  const filteredItems = (panelData?.taksitListesi ?? []).filter(item => {
    if (!tableSearch) return true;
    const lower = tableSearch.toLowerCase();
    return (
      item.musteriAdi.toLowerCase().includes(lower) ||
      item.projeAdi.toLowerCase().includes(lower) ||
      item.odemeTuruDetay.toLowerCase().includes(lower) ||
      (item.sozlesmeTakipNo ?? '').toLowerCase().includes(lower)
    );
  });

  const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE);
  const pagedItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const kpis = panelData?.kpis;

  // ── Türkçe karakter dönüştürme (jsPDF Helvetica fontu için) ──────────────
  const tr2pdf = (s: string | null | undefined): string => (s ?? '')
    .replace(/ğ/g, 'g').replace(/Ğ/g, 'G')
    .replace(/ü/g, 'u').replace(/Ü/g, 'U')
    .replace(/ş/g, 's').replace(/Ş/g, 'S')
    .replace(/ı/g, 'i').replace(/İ/g, 'I')
    .replace(/ö/g, 'o').replace(/Ö/g, 'O')
    .replace(/ç/g, 'c').replace(/Ç/g, 'C');

  // PDF için para formatı (₺ sembolü yerine TL kullanır)
  const formatCurrencyForPDF = (val: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val) + ' TL';
  };

  // ── Export fonksiyonları ────────────────────────────────────────────────
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text(tr2pdf('Taksit Takip Paneli'), 14, 15);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(tr2pdf(`Olusturma: ${new Date().toLocaleDateString('tr-TR')}`), 14, 22);
    autoTable(doc, {
      startY: 27,
      head: [[tr2pdf('Vade Tarihi'), tr2pdf('Musteri Adi'), tr2pdf('Proje'), tr2pdf('Taksit No'), tr2pdf('Taksit Tutari'), tr2pdf('Kalan Tutar'), tr2pdf('Durum')]],
      body: filteredItems.map(item => [
        formatDate(item.vadeTarihi),
        tr2pdf(item.musteriAdi),
        tr2pdf(item.projeAdi),
        tr2pdf(item.odemeTuruDetay),
        formatCurrencyForPDF(item.taksitTutari),
        formatCurrencyForPDF(item.kalanTutar),
        tr2pdf(item.durum),
      ]),
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [230, 125, 33], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save('taksit-takip-paneli.pdf');
  };

  const exportExcel = () => {
    const wsData = [
      ['Vade Tarihi', 'Müşteri Adı', 'Proje', 'Taksit No', 'Taksit Tutarı', 'Kalan Tutar', 'Durum'],
      ...filteredItems.map(item => [
        formatDate(item.vadeTarihi),
        item.musteriAdi,
        item.projeAdi,
        item.odemeTuruDetay,
        item.taksitTutari,
        item.kalanTutar,
        item.durum,
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 14 }, { wch: 24 }, { wch: 24 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Taksit Takip');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'taksit-takip-paneli.xlsx');
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', padding: 24, fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>

      {/* ── Sayfa Başlığı ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 40 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.3px' }}>
            Taksit Takip Paneli
          </h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
            Müşteri ödeme planlarını ve taksit durumlarını takip edin
          </p>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          BÖLÜM 1 — Filtre Alanı
      ══════════════════════════════════════════════════════════════════════ */}
      <div
        style={{
          background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14,
          padding: '20px 24px', marginBottom: 20,
          boxShadow: '0 1px 3px rgba(15,23,42,0.05)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Filtreler</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, alignItems: 'end' }}>

          {/* Proje Seçimi */}
          <div>
            <label style={{ fontSize: 11.5, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Proje
            </label>
            <select
              value={filters.projeID}
              onChange={e => setFilters(prev => ({ ...prev, projeID: e.target.value }))}
              style={inputStyle}
            >
              <option value="">Tüm Projeler</option>
              {projeler.map(p => (
                <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>
              ))}
            </select>
          </div>

          {/* Müşteri Arama */}
          <div style={{ position: 'relative' }}>
            <label style={{ fontSize: 11.5, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Müşteri
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={musteriArama}
                placeholder="Müşteri adı ara..."
                onChange={e => handleMusteriArama(e.target.value)}
                onFocus={() => setShowCariDropdown(true)}
                style={{ ...inputStyle, paddingRight: musteriArama ? 32 : 10 }}
              />
              {musteriArama && (
                <button
                  onClick={clearCariFilter}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, padding: 0 }}
                >×</button>
              )}
            </div>
            {showCariDropdown && filteredCariler.length > 0 && musteriArama && (
              <div
                style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(15,23,42,0.12)', maxHeight: 200, overflowY: 'auto',
                }}
              >
                {filteredCariler.slice(0, 20).map(c => (
                  <div
                    key={c.cariID}
                    onClick={() => selectCari(c)}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: '#374151', borderBottom: '1px solid #f9fafb' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                  >
                    <div style={{ fontWeight: 500 }}>{c.adiSoyadi}</div>
                    {c.telefon1 && <div style={{ fontSize: 11, color: '#9ca3af' }}>{c.telefon1}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Başlangıç Tarihi */}
          <div>
            <label style={{ fontSize: 11.5, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Başlangıç Tarihi
            </label>
            <input
              type="date"
              value={filters.baslangicTarihi}
              onChange={e => setFilters(prev => ({ ...prev, baslangicTarihi: e.target.value }))}
              style={inputStyle}
            />
          </div>

          {/* Bitiş Tarihi */}
          <div>
            <label style={{ fontSize: 11.5, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Bitiş Tarihi
            </label>
            <input
              type="date"
              value={filters.bitisTarihi}
              onChange={e => setFilters(prev => ({ ...prev, bitisTarihi: e.target.value }))}
              style={inputStyle}
            />
          </div>

          {/* Durum Filtresi — çoklu seçim */}
          <div style={{ position: 'relative' }}>
            <label style={{ fontSize: 11.5, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Durum
            </label>
            <button
              type="button"
              onClick={() => setShowDurumDropdown(v => !v)}
              style={{
                ...inputStyle,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 6, cursor: 'pointer', textAlign: 'left',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {filters.odemeDurumu.length === 0
                  ? 'Durum seçin'
                  : filters.odemeDurumu.length === DURUM_OPTIONS.length
                    ? 'Tüm Durumlar'
                    : `${filters.odemeDurumu.length} durum seçili`}
              </span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: showDurumDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {showDurumDropdown && (
              <div
                style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999, marginTop: 4,
                  background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(15,23,42,0.12)', overflow: 'hidden',
                }}
              >
                {DURUM_OPTIONS.map(durum => {
                  const checked = filters.odemeDurumu.includes(durum);
                  return (
                    <label
                      key={durum}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px',
                        cursor: 'pointer', fontSize: 13, color: '#374151',
                        borderBottom: '1px solid #f9fafb',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDurum(durum)}
                        style={{ accentColor: '#e67d21', width: 15, height: 15, cursor: 'pointer' }}
                      />
                      {durum}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Butonlar */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleFiltrele}
              disabled={loading}
              style={{
                flex: 1, padding: '9px 0', borderRadius: 8, border: 'none',
                background: loading ? '#9ca3af' : '#e67d21', color: '#fff',
                fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => !loading && (e.currentTarget.style.background = '#c96a18')}
              onMouseLeave={e => !loading && (e.currentTarget.style.background = '#e67d21')}
            >
              {loading ? (
                <><i className="fas fa-spinner fa-spin" style={{ fontSize: 12 }} /> Yükleniyor</>
              ) : (
                <><i className="fas fa-search" style={{ fontSize: 11 }} /> Filtrele</>
              )}
            </button>
            <button
              onClick={handleTemizle}
              style={{
                padding: '9px 14px', borderRadius: 8, border: '1.5px solid #e5e7eb',
                background: '#fff', color: '#6b7280',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#374151'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
              title="Filtreleri Temizle"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        {/* Backdrop for cari dropdown */}
        {showCariDropdown && (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 998 }}
            onClick={() => setShowCariDropdown(false)}
          />
        )}

        {/* Backdrop for durum dropdown */}
        {showDurumDropdown && (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 998 }}
            onClick={() => setShowDurumDropdown(false)}
          />
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          BÖLÜM 2 — KPI Kartları
      ══════════════════════════════════════════════════════════════════════ */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>

        {/* Gecikmiş Taksitler */}
        <KpiCard
          title="Gecikmiş Taksitler"
          amount={formatCurrency(kpis?.gecikmisToplam ?? 0)}
          count={kpis?.gecikmisSayi ?? 0}
          countLabel="adet taksit"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4l2 2" />
            </svg>
          }
          accent="#dc2626"
          bg="#fff5f5"
          border="#fecaca"
          iconBg="#fee2e2"
          active={filters.odemeDurumu.length === 1 && filters.odemeDurumu[0] === 'Gecikmiş'}
          onClick={() => handleKpiClick('Gecikmiş')}
          loading={loading}
        />

        {/* Bugün Gelen Ödemeler */}
        <KpiCard
          title="Bugün Vadesi Dolan"
          amount={formatCurrency(kpis?.bugunToplam ?? 0)}
          count={kpis?.bugunSayi ?? 0}
          countLabel="adet taksit"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e67d21" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
            </svg>
          }
          accent="#e67d21"
          bg="#fff7ed"
          border="#fed7aa"
          iconBg="#ffedd5"
          active={filters.odemeDurumu.length === 1 && filters.odemeDurumu[0] === 'Bugün'}
          onClick={() => handleKpiClick('Bugün')}
          loading={loading}
        />

        {/* Toplam Beklenen */}
        <KpiCard
          title="Toplam Beklenen"
          amount={formatCurrency(kpis?.toplamBeklenen ?? 0)}
          count={kpis?.toplamBeklenenSayi ?? 0}
          countLabel="bekleyen taksit"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          }
          accent="#2563eb"
          bg="#eff6ff"
          border="#bfdbfe"
          iconBg="#dbeafe"
          active={filters.odemeDurumu.length === 1 && filters.odemeDurumu[0] === 'Bekleniyor'}
          onClick={() => handleKpiClick('Bekleniyor')}
          loading={loading}
        />
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          BÖLÜM 3 — Detaylı Taksit Tablosu
      ══════════════════════════════════════════════════════════════════════ */}
      <div
        style={{
          background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14,
          boxShadow: '0 1px 3px rgba(15,23,42,0.05)', overflow: 'hidden',
        }}
      >
        {/* Tablo Başlığı */}
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 24px', borderBottom: '1px solid #f1f5f9',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
              Taksit Listesi
            </span>
            {!loading && panelData && (
              <span
                style={{
                  background: '#f3f4f6', color: '#6b7280', borderRadius: 20,
                  fontSize: 11, fontWeight: 600, padding: '2px 8px',
                }}
              >
                {filteredItems.length} kayıt
              </span>
            )}
          </div>

          {/* Export butonları */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={exportExcel}
              disabled={filteredItems.length === 0}
              title="Excel olarak indir"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 8, border: '1.5px solid #16a34a',
                background: '#f0fdf4', color: '#16a34a', fontSize: 12.5, fontWeight: 600,
                cursor: filteredItems.length === 0 ? 'not-allowed' : 'pointer',
                opacity: filteredItems.length === 0 ? 0.5 : 1, fontFamily: 'inherit',
                transition: 'background 0.15s',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
              Excel
            </button>
            <button
              onClick={exportPDF}
              disabled={filteredItems.length === 0}
              title="PDF olarak indir"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 8, border: '1.5px solid #dc2626',
                background: '#fef2f2', color: '#dc2626', fontSize: 12.5, fontWeight: 600,
                cursor: filteredItems.length === 0 ? 'not-allowed' : 'pointer',
                opacity: filteredItems.length === 0 ? 0.5 : 1, fontFamily: 'inherit',
                transition: 'background 0.15s',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="12" y1="18" x2="12" y2="12" />
                <line x1="9" y1="15" x2="15" y2="15" />
              </svg>
              PDF
            </button>

            {/* Tablo içi arama */}
            <div style={{ position: 'relative' }}>
            <i className="fas fa-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 12 }} />
            <input
              type="text"
              placeholder="Tabloda ara..."
              value={tableSearch}
              onChange={e => { setTableSearch(e.target.value); setCurrentPage(1); }}
              style={{
                paddingLeft: 30, paddingRight: 10, paddingTop: 7, paddingBottom: 7,
                border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 12.5,
                outline: 'none', color: '#374151', background: '#fafafa',
                width: 200,
              }}
            />
            </div>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#9ca3af' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, marginBottom: 12, display: 'block', color: '#e67d21' }} />
            <div style={{ fontSize: 13 }}>Veriler yükleniyor...</div>
          </div>
        )}

        {/* Empty state */}
        {!loading && (!panelData || filteredItems.length === 0) && (
          <div style={{ padding: '60px 0', textAlign: 'center', color: '#9ca3af' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px', display: 'block' }}>
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <path d="M16 2v4M8 2v4M3 10h18" />
              <path d="M8 14h.01M12 14h.01M16 14h.01" />
            </svg>
            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>
              {!panelData ? 'Verileri görmek için filtrele butonuna basın' : 'Taksit bulunamadı'}
            </div>
            {!panelData && (
              <button
                onClick={handleFiltrele}
                style={{
                  marginTop: 12, padding: '8px 20px', borderRadius: 8,
                  border: 'none', background: '#e67d21', color: '#fff',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                <i className="fas fa-search" style={{ marginRight: 6, fontSize: 11 }} />
                Tüm Taksitleri Getir
              </button>
            )}
          </div>
        )}

        {/* Table */}
        {!loading && filteredItems.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1.5px solid #e5e7eb' }}>
                    {['Vade Tarihi', 'Müşteri Adı', 'Proje', 'Taksit No', 'Taksit Tutarı', 'Kalan Tutar', 'Durum', 'İşlem'].map(col => (
                      <th
                        key={col}
                        style={{
                          padding: '11px 16px', textAlign: 'left', fontSize: 11,
                          fontWeight: 700, color: '#6b7280', textTransform: 'uppercase',
                          letterSpacing: '0.05em', whiteSpace: 'nowrap',
                        }}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedItems.map((item, idx) => (
                    <TableRow
                      key={item.taksitID}
                      item={item}
                      idx={idx}
                      onDetay={() => {
                        if (item.sozlesmeTakipNo) {
                          navigate(`/sozlesme-detay/${item.sozlesmeTakipNo}`);
                        } else {
                          toast.info('Sözleşme takip no bulunamadı');
                        }
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Sayfalama */}
            {totalPages > 1 && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 24px', borderTop: '1px solid #f1f5f9',
                }}
              >
                <span style={{ fontSize: 12.5, color: '#6b7280' }}>
                  Sayfa {currentPage} / {totalPages} — Toplam {filteredItems.length} kayıt
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <PageBtn disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
                    <i className="fas fa-chevron-left" style={{ fontSize: 11 }} />
                  </PageBtn>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let page = i + 1;
                    if (totalPages > 5) {
                      if (currentPage <= 3) page = i + 1;
                      else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                      else page = currentPage - 2 + i;
                    }
                    return (
                      <PageBtn key={page} active={currentPage === page} onClick={() => setCurrentPage(page)}>
                        {page}
                      </PageBtn>
                    );
                  })}
                  <PageBtn disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
                    <i className="fas fa-chevron-right" style={{ fontSize: 11 }} />
                  </PageBtn>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 10px', border: '1.5px solid #e5e7eb',
  borderRadius: 8, fontSize: 13, outline: 'none',
  color: '#374151', background: '#fff', boxSizing: 'border-box',
  fontFamily: 'inherit',
};

interface KpiCardProps {
  title: string;
  amount: string;
  count: number;
  countLabel: string;
  icon: React.ReactNode;
  accent: string;
  bg: string;
  border: string;
  iconBg: string;
  active: boolean;
  onClick: () => void;
  loading: boolean;
}

const KpiCard: React.FC<KpiCardProps> = ({
  title, amount, count, countLabel, icon, accent, bg, border, iconBg, active, onClick, loading,
}) => (
  <div
    onClick={onClick}
    style={{
      background: bg,
      border: `1.5px solid ${active ? accent : border}`,
      borderRadius: 14, padding: '20px 22px',
      boxShadow: active ? `0 0 0 3px ${accent}22, 0 2px 8px rgba(15,23,42,0.06)` : '0 1px 3px rgba(15,23,42,0.05)',
      cursor: 'pointer', transition: 'all 0.18s',
      position: 'relative', overflow: 'hidden',
    }}
    onMouseEnter={e => !active && (e.currentTarget.style.boxShadow = `0 4px 12px rgba(15,23,42,0.10)`)}
    onMouseLeave={e => !active && (e.currentTarget.style.boxShadow = '0 1px 3px rgba(15,23,42,0.05)')}
  >
    {active && (
      <div
        style={{
          position: 'absolute', top: 8, right: 10, background: accent,
          color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: 4,
          padding: '2px 6px', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}
      >
        Filtre Aktif
      </div>
    )}
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: '#6b7280', letterSpacing: '-0.01em' }}>{title}</span>
      <div style={{ width: 36, height: 36, borderRadius: 9, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </div>
    </div>
    {loading ? (
      <div style={{ height: 32, background: `${accent}22`, borderRadius: 6, marginBottom: 8 }} />
    ) : (
      <div style={{ fontSize: 26, fontWeight: 800, color: accent, letterSpacing: '-0.5px', fontVariantNumeric: 'tabular-nums', marginBottom: 6 }}>
        {amount}
      </div>
    )}
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          background: `${accent}18`, color: accent, borderRadius: 6,
          fontSize: 11.5, fontWeight: 700, padding: '2px 8px',
        }}
      >
        {loading ? '...' : count}
      </span>
      <span style={{ fontSize: 11.5, color: '#9ca3af' }}>{countLabel}</span>
    </div>
  </div>
);

interface TableRowProps {
  item: TaksitItem;
  idx: number;
  onDetay: () => void;
}

const TableRow: React.FC<TableRowProps> = ({ item, idx, onDetay }) => {
  const isOverdue = item.durum === 'Gecikmiş';
  const isToday = item.durum === 'Bugün';

  return (
    <tr
      style={{
        background: isOverdue ? '#fff9f9' : isToday ? '#fffbf5' : idx % 2 === 0 ? '#ffffff' : '#fafafa',
        borderBottom: '1px solid #f1f5f9',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
      onMouseLeave={e => (e.currentTarget.style.background = isOverdue ? '#fff9f9' : isToday ? '#fffbf5' : idx % 2 === 0 ? '#ffffff' : '#fafafa')}
    >
      {/* Vade Tarihi */}
      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: isOverdue ? '#dc2626' : isToday ? '#e67d21' : '#1e293b' }}>
          {formatDate(item.vadeTarihi)}
        </div>
        {isOverdue && (
          <div style={{ fontSize: 10.5, color: '#ef4444', fontWeight: 500 }}>
            {Math.floor((new Date().getTime() - new Date(item.vadeTarihi).getTime()) / (1000 * 60 * 60 * 24))} gün gecikmiş
          </div>
        )}
      </td>

      {/* Müşteri Adı */}
      <td style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
              background: '#f0f9ff', border: '1px solid #bae6fd',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: '#0284c7',
            }}
          >
            {item.musteriAdi.charAt(0).toUpperCase()}
          </div>
          <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{item.musteriAdi}</span>
        </div>
      </td>

      {/* Proje */}
      <td style={{ padding: '12px 16px' }}>
        <span style={{ fontSize: 12.5, color: '#4b5563', fontWeight: 500 }}>{item.projeAdi}</span>
      </td>

      {/* Taksit No */}
      <td style={{ padding: '12px 16px' }}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>
          {item.odemeTuruDetay} #{item.siraNo}
        </span>
      </td>

      {/* Taksit Tutarı */}
      <td style={{ padding: '12px 16px' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827', fontVariantNumeric: 'tabular-nums' }}>
          {formatCurrency(item.taksitTutari)}
        </span>
      </td>

      {/* Kalan Tutar */}
      <td style={{ padding: '12px 16px' }}>
        <span style={{
          fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
          color: item.kalanTutar === 0 ? '#16a34a' : '#e67d21',
        }}>
          {item.kalanTutar === 0 ? '✓ Ödendi' : formatCurrency(item.kalanTutar)}
        </span>
      </td>

      {/* Durum */}
      <td style={{ padding: '12px 16px' }}>
        <StatusBadge durum={item.durum} />
      </td>

      {/* İşlem */}
      <td style={{ padding: '12px 16px' }}>
        <button
          onClick={onDetay}
          style={{
            padding: '6px 12px', borderRadius: 7,
            border: '1.5px solid #e5e7eb', background: '#fff',
            color: '#374151', fontSize: 11.5, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', gap: 5,
            transition: 'all 0.15s', whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#e67d21';
            e.currentTarget.style.borderColor = '#e67d21';
            e.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = '#fff';
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.color = '#374151';
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          Ödeme Detayı Gör
        </button>
      </td>
    </tr>
  );
};

interface PageBtnProps {
  children: React.ReactNode;
  disabled?: boolean;
  active?: boolean;
  onClick: () => void;
}

const PageBtn: React.FC<PageBtnProps> = ({ children, disabled, active, onClick }) => (
  <button
    disabled={disabled}
    onClick={onClick}
    style={{
      minWidth: 30, height: 30, borderRadius: 6,
      border: active ? 'none' : '1.5px solid #e5e7eb',
      background: active ? '#e67d21' : '#fff',
      color: active ? '#fff' : disabled ? '#d1d5db' : '#374151',
      fontSize: 12.5, fontWeight: active ? 700 : 500,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: 'inherit',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
  >
    {children}
  </button>
);

export default TaksitTakipPaneli;
